import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Search, X, Check, ChevronRight, ChevronLeft, Trophy, Shield, Star, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const DIVISIONS = [
  { id: "Allsvenskan", label: "Allsvenskan", desc: "Högsta serien" },
  { id: "Superettan", label: "Superettan", desc: "Näst högsta" },
  { id: "Division 1", label: "Division 1", desc: "Ettan Nord/Syd" },
  { id: "Division 2", label: "Division 2", desc: "Regionala grupper" },
  { id: "Division 3", label: "Division 3", desc: "Lokala grupper" },
  { id: "Division 4", label: "Division 4", desc: "Lokala grupper" },
  { id: "Division 5", label: "Division 5", desc: "Lokala grupper" },
];

const TOKEN_KEY = 'fotbollsliv_session_token';

function authHeaders() {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  } catch {
    return { 'Content-Type': 'application/json' };
  }
}

export default function OnboardingModal() {
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState(0);
  const [teamSearch, setTeamSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedDivisions, setSelectedDivisions] = useState(["Allsvenskan"]);
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);

  const searchTeams = useCallback(async (q) => {
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${API}/api/teams?search=${encodeURIComponent(q)}&limit=10`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.teams || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchTeams(teamSearch), 300);
    return () => clearTimeout(timer);
  }, [teamSearch, searchTeams]);

  const toggleTeam = (team) => {
    setSelectedTeams(prev => {
      const exists = prev.find(t => t.id === team.id);
      if (exists) return prev.filter(t => t.id !== team.id);
      if (prev.length >= 3) return prev;
      return [...prev, team];
    });
  };

  const toggleDivision = (divId) => {
    setSelectedDivisions(prev =>
      prev.includes(divId) ? prev.filter(d => d !== divId) : [...prev, divId]
    );
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/user/complete-onboarding`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          followed_teams: selectedTeams.map(t => t.id),
          favorite_divisions: selectedDivisions,
        }),
      });
      if (res.ok) {
        await checkAuth();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/user/complete-onboarding`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ followed_teams: [], favorite_divisions: [] }),
      });
      await checkAuth();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (!user || user.onboarding_completed) return null;

  const steps = [
    // Step 0: Welcome
    <div key="welcome" className="text-center space-y-6" data-testid="onboarding-step-welcome">
      <div className="w-16 h-16 bg-[#FECC00] rounded-2xl flex items-center justify-center mx-auto">
        <Trophy className="w-8 h-8 text-[#0F172A]" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-[#0F172A]">Välkommen till Fotbollsliv!</h2>
        <p className="text-slate-500 mt-2 max-w-sm mx-auto">
          Låt oss göra upplevelsen personlig. Det tar bara 30 sekunder.
        </p>
      </div>
      <Button onClick={() => setStep(1)} className="bg-[#0F172A] hover:bg-slate-800 text-white px-8" data-testid="onboarding-start-btn">
        Kom igång <ChevronRight className="w-4 h-4 ml-1" />
      </Button>
    </div>,

    // Step 1: Choose teams
    <div key="teams" className="space-y-4" data-testid="onboarding-step-teams">
      <div className="text-center">
        <h2 className="text-xl font-bold text-[#0F172A]">Välj favoritlag</h2>
        <p className="text-slate-500 text-sm mt-1">Välj upp till 3 lag att följa</p>
      </div>

      {selectedTeams.length > 0 && (
        <div className="flex flex-wrap gap-2 justify-center">
          {selectedTeams.map(t => (
            <Badge key={t.id} variant="secondary" className="flex items-center gap-1.5 py-1.5 px-3 bg-[#FECC00]/20 text-[#0F172A] border border-[#FECC00]/50">
              {t.logo_url && <img src={t.logo_url} alt="" className="w-4 h-4 object-contain" />}
              {t.name}
              <button onClick={() => toggleTeam(t)} className="ml-1"><X className="w-3 h-3" /></button>
            </Badge>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Sök lag... (t.ex. AIK, Hammarby)"
          value={teamSearch}
          onChange={(e) => setTeamSearch(e.target.value)}
          className="pl-10"
          data-testid="onboarding-team-search"
        />
        {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
      </div>

      {searchResults.length > 0 && (
        <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2">
          {searchResults.map(team => {
            const isSelected = selectedTeams.some(t => t.id === team.id);
            return (
              <button
                key={team.id}
                onClick={() => toggleTeam(team)}
                disabled={!isSelected && selectedTeams.length >= 3}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors
                  ${isSelected ? 'bg-[#FECC00]/20 border border-[#FECC00]/50' : 'hover:bg-slate-50'}
                  ${!isSelected && selectedTeams.length >= 3 ? 'opacity-40 cursor-not-allowed' : ''}
                `}
                data-testid={`onboarding-team-${team.id}`}
              >
                {team.logo_url && <img src={team.logo_url} alt="" className="w-5 h-5 object-contain shrink-0" />}
                <span className="flex-1 font-medium">{team.name}</span>
                <span className="text-xs text-slate-400">{team.division}</span>
                {isSelected && <Check className="w-4 h-4 text-[#FECC00]" />}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex justify-between pt-2">
        <Button variant="ghost" size="sm" onClick={() => setStep(0)} data-testid="onboarding-back-0">
          <ChevronLeft className="w-4 h-4 mr-1" /> Tillbaka
        </Button>
        <Button onClick={() => setStep(2)} className="bg-[#0F172A] hover:bg-slate-800 text-white" data-testid="onboarding-next-2">
          {selectedTeams.length === 0 ? 'Hoppa över' : 'Nästa'} <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>,

    // Step 2: Choose divisions
    <div key="divisions" className="space-y-4" data-testid="onboarding-step-divisions">
      <div className="text-center">
        <h2 className="text-xl font-bold text-[#0F172A]">Välj divisioner</h2>
        <p className="text-slate-500 text-sm mt-1">Vilka ligor vill du följa?</p>
      </div>

      <div className="space-y-2">
        {DIVISIONS.map(div => {
          const isSelected = selectedDivisions.includes(div.id);
          return (
            <button
              key={div.id}
              onClick={() => toggleDivision(div.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-all
                ${isSelected ? 'bg-[#FECC00]/10 border-[#FECC00] shadow-sm' : 'border-slate-200 hover:border-slate-300'}
              `}
              data-testid={`onboarding-div-${div.id}`}
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0
                ${isSelected ? 'bg-[#FECC00] border-[#FECC00]' : 'border-slate-300'}
              `}>
                {isSelected && <Check className="w-3 h-3 text-[#0F172A]" />}
              </div>
              <div className="flex-1">
                <span className="font-medium text-sm">{div.label}</span>
                <span className="text-xs text-slate-400 ml-2">{div.desc}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex justify-between pt-2">
        <Button variant="ghost" size="sm" onClick={() => setStep(1)} data-testid="onboarding-back-1">
          <ChevronLeft className="w-4 h-4 mr-1" /> Tillbaka
        </Button>
        <Button onClick={() => setStep(3)} className="bg-[#0F172A] hover:bg-slate-800 text-white" data-testid="onboarding-next-3">
          Nästa <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>,

    // Step 3: Done
    <div key="done" className="text-center space-y-6" data-testid="onboarding-step-done">
      <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto">
        <Star className="w-8 h-8 text-green-600" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-[#0F172A]">Allt klart!</h2>
        <p className="text-slate-500 mt-2 max-w-sm mx-auto">
          {selectedTeams.length > 0
            ? `Du följer nu ${selectedTeams.map(t => t.name).join(', ')}. `
            : ''}
          {selectedDivisions.length > 0
            ? `Du bevakar ${selectedDivisions.length} division${selectedDivisions.length > 1 ? 'er' : ''}.`
            : 'Du kan alltid ändra dina inställningar i profilen.'}
        </p>
      </div>
      <Button
        onClick={handleComplete}
        disabled={saving}
        className="bg-[#FECC00] hover:bg-yellow-400 text-[#0F172A] font-bold px-8"
        data-testid="onboarding-complete-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
        Starta upplevelsen
      </Button>
    </div>,
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="onboarding-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 relative max-h-[90vh] overflow-y-auto">
        {/* Skip button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 text-xs"
          data-testid="onboarding-skip-btn"
        >
          Hoppa över
        </button>

        {/* Progress */}
        <div className="flex gap-1.5 mb-6">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= step ? 'bg-[#FECC00]' : 'bg-slate-200'}`} />
          ))}
        </div>

        {steps[step]}
      </div>
    </div>
  );
}
